'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

interface ServiceCard {
  icon: React.ReactNode;
  badge: string;
  badgeBg: string;
  badgeColor: string;
  subtitle: string;
  subtitleColor: string;
  title: string;
  description: string;
  tags: string[];
  tagBg: string;
  tagColor: string;
  colSpan?: string;
}

const services: ServiceCard[] = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    ),
    badge: 'PRIMARY',
    badgeBg: '#F5A62315',
    badgeColor: '#F5A623',
    subtitle: 'iOS & Android apps that users love',
    subtitleColor: '#F5A623',
    title: 'App Development',
    description: 'We design and build cross-platform mobile apps — from MVPs to full-scale products. React Native and native builds, App Store ready, with clean UX that drives retention and in-app revenue.',
    tags: ['iOS & Android', 'React Native / Flutter', 'MVP to Full Product', 'App Store Optimisation', 'In-App Monetisation'],
    tagBg: '#F5A62310',
    tagColor: '#F5A623',
    colSpan: 'col-span-1 md:col-span-2',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
    badge: 'PRIMARY',
    badgeBg: '#F5A62315',
    badgeColor: '#F5A623',
    subtitle: 'Custom sites built to convert',
    subtitleColor: '#F5A623',
    title: 'Web Development',
    description: 'Fast, SEO-ready websites and landing pages that turn visitors into customers. From business sites to e-commerce and lead-gen funnels — every pixel crafted for performance.',
    tags: ['Landing Pages & Funnels', 'Business & E-commerce', 'CMS & Blog Platforms', 'Performance Optimised'],
    tagBg: '#F5A62310',
    tagColor: '#F5A623',
    colSpan: 'col-span-1',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      </svg>
    ),
    badge: 'HIGH ROI',
    badgeBg: '#7C3AED15',
    badgeColor: '#7C3AED',
    subtitle: 'Social ads that scale revenue',
    subtitleColor: '#7C3AED',
    title: 'Meta Ads (Facebook & Instagram)',
    description: 'Precision-targeted Facebook and Instagram campaigns that reach buyers at every stage of the funnel. Creative strategy, audience testing, and ROAS-focused optimisation — all handled.',
    tags: ['Facebook & Instagram', 'Audience Targeting', 'Creative Strategy', 'Retargeting Funnels', 'ROAS Optimisation'],
    tagBg: '#7C3AED10',
    tagColor: '#7C3AED',
    colSpan: 'col-span-1',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
      </svg>
    ),
    badge: 'TRENDING',
    badgeBg: '#EC489915',
    badgeColor: '#EC4899',
    subtitle: 'Viral reach, measurable results',
    subtitleColor: '#EC4899',
    title: 'TikTok Ads',
    description: "TikTok's algorithm rewards great creative — we produce and manage short-form video ad campaigns that capture attention, drive clicks, and convert at scale for products and apps.",
    tags: ['In-Feed & TopView Ads', 'UGC-Style Creatives', 'Spark Ads', 'App Install Campaigns', 'Conversion Tracking'],
    tagBg: '#EC489910',
    tagColor: '#EC4899',
    colSpan: 'col-span-1',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    badge: 'CERTIFIED',
    badgeBg: '#10B98115',
    badgeColor: '#10B981',
    subtitle: 'Qualified traffic, measurable ROI',
    subtitleColor: '#10B981',
    title: 'Google Ads Management',
    description: 'Search, Display, Shopping, and Performance Max campaigns managed by certified experts. We cut wasted spend and scale what works — every rupee tracked to revenue.',
    tags: ['Search & Display', 'Performance Max', 'Shopping Campaigns', 'Conversion Tracking', 'Bid Optimisation'],
    tagBg: '#10B98110',
    tagColor: '#10B981',
    colSpan: 'col-span-1',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    badge: 'ADD-ON',
    badgeBg: '#6B728015',
    badgeColor: '#6B7280',
    subtitle: 'Passive commissions on the side',
    subtitleColor: '#6B7280',
    title: 'Affiliate Marketing',
    description: 'We layer affiliate content strategies — product reviews, comparison pages, and niche sites — as an additional passive income stream alongside your primary app, web, and ads revenue.',
    tags: ['Niche Site Strategy', 'Amazon & CJ Affiliates', 'SEO-Optimised Pages'],
    tagBg: '#6B728010',
    tagColor: '#6B7280',
    colSpan: 'col-span-1',
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const reveals = sectionRef.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-0 w-[400px] h-[400px] rounded-full animate-blob"
          style={{
            background: 'radial-gradient(circle, rgba(245,166,35,0.06) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="mb-16 reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-accent/20 bg-accent/5 mb-6">
            <span className="text-xs font-700 text-accent tracking-widest uppercase">What We Do</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2
              className="font-display font-800 text-foreground tracking-tight"
              style={{
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 800,
                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                lineHeight: 1.1,
              }}
            >
              Build. Launch.<br />
              <span className="text-gradient-amber">Scale with Ads.</span>
            </h2>
            <p className="text-muted font-400 leading-relaxed max-w-md">
              From the first line of code to your first million impressions — we build apps, websites, and run the paid ads that fill them with buyers.
            </p>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {services.map((service, i) => (
            <div
              key={service.title}
              className={`${service.colSpan} glass-card glass-card-hover rounded-3xl p-7 md:p-8 reveal reveal-delay-${i + 1} flex flex-col`}
            >
              <div className="flex items-start justify-between mb-5">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: service.badgeBg, color: service.badgeColor }}
                >
                  {service.icon}
                </div>
                <span
                  className="text-xs font-700 px-2.5 py-1 rounded-full tracking-widest"
                  style={{ background: service.badgeBg, color: service.badgeColor }}
                >
                  {service.badge}
                </span>
              </div>
              <div
                className="text-xs font-700 tracking-widest uppercase mb-2"
                style={{ color: service.subtitleColor }}
              >
                {service.subtitle}
              </div>
              <h3
                className="text-xl font-700 text-foreground mb-3 font-display"
                style={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700 }}
              >
                {service.title}
              </h3>
              <p className="text-muted text-sm leading-relaxed mb-5 flex-1">{service.description}</p>
              <ul className="flex flex-wrap gap-2">
                {service.tags.map((tag) => (
                  <li
                    key={tag}
                    className="text-xs font-500 px-2.5 py-1 rounded-full"
                    style={{ background: service.tagBg, color: service.tagColor }}
                  >
                    {tag}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center reveal">
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-primary text-base font-700 rounded-full hover:bg-accent-light transition-all duration-300 hover:-translate-y-1 accent-glow"
            style={{ color: '#0d0d1a', fontWeight: 700 }}
          >
            Discuss Your Project
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
